import { Select, Space, Typography } from "antd"
import { Status, buildStatusList, getWorkareaIdsThatShouldShowStatus, getWorkareaIdsThatShouldShowJobStatus } from "./status"
import { useJobQuery, useVisitQuery, useVisitWorkareaStatuses } from "./apiQueries"
import { useContext } from "react"
import EditVisitContext from "./EditVisitContext"
import { getJobId } from "./editVisitModel"
import { getIdsOfWorkareasWhichHaveTimesheets } from "./labor"
import { PricingTypes, ProjectStatus, ProjectStatuses, VisitWorkareaStatus, VisitWorkareaStatuses } from "../../common/constants"
import { Job } from "./editVisitApi"
import WorkareaLabel from "./WorkareaLabel"

const { Title } = Typography

const visitStatusOptions = [
  { label: 'In Progress', value: VisitWorkareaStatuses.InProgress },
  { label: 'Completed', value: VisitWorkareaStatuses.Completed },
  { label: 'Completed (Unbillable)', value: VisitWorkareaStatuses.CompletedUnbillable },
  { label: 'Skipped (Reschedule)', value: VisitWorkareaStatuses.SkippedReschedule },
  { label: "Skipped (Don't Reschedule)", value: VisitWorkareaStatuses.Skipped },
]

const jobStatusOptions = [
  { label: 'Unscheduled', value: ProjectStatuses.Unscheduled },
  { label: 'Scheduled', value: ProjectStatuses.Scheduled },
  { label: 'Blocked / Waiting on Parts', value: ProjectStatuses.WaitingOnParts },
  { label: 'Completed', value: ProjectStatuses.Completed },
]

const getName = (status: Status, job?: Job) => {
  if (status.workareaId) {
    const match = job?.workareas.find(x => x.id === status.workareaId)
    if (match) {
      return <WorkareaLabel workarea={match} />
    }
  }
  return ''
}

const StatusSection = () => {
  const data = useContext(EditVisitContext)
  const visitQuery = useVisitQuery(data.visitId)
  const jobId = getJobId(data, visitQuery.data)
  const jobQuery = useJobQuery(jobId, data.visitId)
  const visitWorkareaStatusesQuery = useVisitWorkareaStatuses(data.visitId)

  const emptyVisit = { id: '', jobId: 0, visitDate: '', timesheets: [] }
  const timesheetWorkareaIds = getIdsOfWorkareasWhichHaveTimesheets(visitQuery.data ?? emptyVisit, data.laborChanges)

  const workareaIdsThatShouldShowVisitStatus = jobQuery.data ? getWorkareaIdsThatShouldShowStatus(jobQuery.data) : []
  const visitStatusWorkareaIds = timesheetWorkareaIds.filter(x => workareaIdsThatShouldShowVisitStatus.includes(x))
  const visitStatusList = jobQuery.data ? buildStatusList(jobQuery.data, visitWorkareaStatusesQuery.data ?? [], data.statusChanges, visitStatusWorkareaIds) : []

  const workareaIdsThatShouldShowJobStatus = jobQuery.data ? getWorkareaIdsThatShouldShowJobStatus(jobQuery.data) : []
  const jobStatusWorkareaIds = timesheetWorkareaIds.filter(x => workareaIdsThatShouldShowJobStatus.includes(x))
  const jobStatusWorkareas = jobQuery.data
    ? jobStatusWorkareaIds.map(id => {
      const workarea = jobQuery.data!.workareas.find(x => x.id === id)
      const change = data.jobStatusChanges.find(x => x.workareaId === id)
      const status = change?.status ?? workarea?.status
      return {
        workareaId: id,
        name: workarea?.name ?? '',
        status,
        changed: status !== workarea?.status
      }
    })
    : []

  if (visitStatusList.length === 0 && jobStatusWorkareas.length === 0) {
    return null
  }

  return <Space direction="vertical">
    <Title level={5}>Status</Title>
    {visitStatusList.map(x => {
      const workarea = jobQuery.data?.workareas.find(y => x.workareaId === y.id)
      return <Space key={x.workareaId}>
        {getName(x, jobQuery.data)}
        <Select
          placeholder="Select a status..."
          value={x.status}
          options={visitStatusOptions.filter(y => workarea?.pricingType === PricingTypes.PerVisit || y.value !== VisitWorkareaStatuses.CompletedUnbillable)}
          status={x.changed ? "warning" : undefined}
          popupMatchSelectWidth={false}
          onChange={newValue => {
            data.dispatch({ type: 'WORKAREA_STATUS_CHANGED', workareaId: x.workareaId!, payload: newValue as VisitWorkareaStatus })
          }}
        />
      </Space>
    })}
    {jobStatusWorkareas.map(x => {
      const workarea = jobQuery.data?.workareas.find(y => x.workareaId === y.id)
      return <Space key={x.workareaId}>
        {workarea ? <WorkareaLabel workarea={workarea} /> : ''}
        <Select
          placeholder="Select a status..."
          value={x.status}
          options={jobStatusOptions}
          status={x.changed ? "warning" : undefined}
          popupMatchSelectWidth={false}
          onChange={newValue => {
            data.dispatch({ type: 'JOB_WORKAREA_STATUS_CHANGED', workareaId: x.workareaId, payload: newValue as ProjectStatus })
          }}
        />
      </Space>
    })}
  </Space>
}
export default StatusSection
